<template>
    <div id="app">
        <toast-container/>
        <navigation-bar
          @stepCompleted="goto"
          v-if="!isQuotePage"
        />
        <notification-bar
          type="info"
          actionText="Learn more"
          @onClose="handleCloseNotification"
          v-if="isNotificationVisible"
        >
          <p>
            <b>Attention: </b>
            Google is shaking things up. <b>Starting on Oct 23, 2023</b>, students
            (under 18) can't use Google SSO for unauthorized school apps. This means
            your students might have trouble signing in.
          </p>
        </notification-bar>
        <GenericNotification
          v-if="isGoogleSSOPopupVisible"
          name="google-sso-warning"
          image="/assets/app/class/google_owl.svg"
          title="Are your students signing in with Google?"
          subtitle="👉 Recommended Action:"
          actionText="Request Log In Cards"
          :notification-bar="{
            type: 'warning',
            actions: false
          }"
          @onSkip="handleSkipNotification"
          @onAction="handleNotificationAction"
          >
            <span>
              We can generate passwords for you and your students and email you Log In Cards for them. This allows you to use
              a standard username and password login method.
              For more details,
              <a
                target="_blank"
                href="https://readtheory.org/teachers-lounge/important-google-sso-access-changes-for-students/"
              >
                check out this blog.
              </a>
            </span>
            <template #notification>
              <span>
                Google is shaking things up. <b>Starting on Oct 23, 2023</b>, students (under 18) can't use Google SSO for unauthorized school apps. This means your students might have trouble signing in.
              </span>
            </template>
          </GenericNotification>
        <PageBody>
            <subscription-result-modal :type="subscriptionResultType" @close="subscriptionResultType=null"></subscription-result-modal>
            <router-view @stepCompleted="goto"></router-view>
        </PageBody>
        <slot></slot>
    </div>
</template>

<script>
  import { mapState } from 'vuex'
  import log from 'common/Logger'
  import SubscriptionResultModal from './teacher/SubscriptionResultModal.vue';
  import NavigationBar from './common/navigation/NavigationBar.vue';
  import PageBody from './common/AppBody.vue';
  import ToastContainer from './common/ToastContainer.vue';
  import Toast from 'common/Toast';
  import bridge from 'common/bridge';
  import { model } from './signup/sign_up_model';
  import Utils from '../../common/Utils';
  import { constants } from 'config';
  import GenericNotification from './common/notifications/GenericNotification.vue';
  import { getUser } from 'common/UserService';
  import {
    getNotificationStatus,
    updateNotificationStatus,
    approveNotification,
    skipNotification,
  } from "common/api";

  export default {
    data: function() {
      return {
        model,
        isQuotePage: false,
        subscriptionResultType: null,
        isNotificationVisible: false,
        isGoogleSSOPopupVisible: false
      }
    },
    components: {
      PageBody,
      ToastContainer,
      SubscriptionResultModal,
      NavigationBar,
      GenericNotification
    },
    computed: {
      ...mapState('classes', ['current_class']),
    },
    created() {
      log.info('Vue App -> created()')
    },
    mounted() {
      if (location.pathname.includes('sales')) {
        this.isQuotePage = true;
      }

        log.info('Vue App -> mounted()')
        this.subscriptionResultType = Utils.getUrlParams()['sub'];
        const pageData = bridge.page_data;
        if (pageData && (pageData.actionSuccessMessage || pageData.message)) {
            if (pageData.actionSuccessMessage) {
                Toast.success(pageData.actionSuccessMessage);
            } else {
                Toast.error(pageData.message);
            }
        }

    if (getUser() && !this.isQuotePage) {
      //This call generated many errors on the service side
      // so it is disabled until needed
      // this.checkNotification();
    }
    },
    methods: {
      async checkNotification() {
        try {
          const notificationStatus = await getNotificationStatus();

          if (notificationStatus) {
            if (notificationStatus.ribbon) {
              this.isNotificationVisible = true;
            }

            if (notificationStatus.popup) {
              this.isGoogleSSOPopupVisible = true;
              this.updateNotificationSeenStatus();
            }
          }
        } catch (e) {
          console.log(e);
        }
      },
      async updateNotificationSeenStatus() {
        try {
          await updateNotificationStatus();
        } catch (e) {
          console.log(e);
        }
      },
      next() {
        this.model.step++
      },
      goto(params) {
        const step = params.step;
        const query = this.$route.query || {};
        if (params.abtest) {
          query[constants.AB_TEST_NAME] = params.abtest;
        }

        this.model.step = step;
        if (step === 'create-account') {
          this.$router.push({name: step, params: {role: this.model.role}, query});
        } else if (step === 'class-detail') {
          if (this.current_class) {
            this.$router.push({name: step, params: {id: this.current_class.id}});
          } else {
            this.$router.push({name: step});
          }
        } else {
          this.$router.push({name: step, query});
        }
      },
      handleCloseNotification() {
        this.isNotificationVisible = false;
      },
      async updateNotificationApproved() {
        try {
          return await approveNotification();
        } catch (e) {
          console.log(e);
          return false;
        }
      },
      async updateNotificationSkipped() {
        try {
          await skipNotification();
        } catch (e) {
          console.log(e);
        }
      },
      handleSkipNotification() {
        this.isGoogleSSOPopupVisible = false;
        this.updateNotificationSkipped();
      },
      async handleNotificationAction() {
        this.isGoogleSSOPopupVisible = false;

        const result = await this.updateNotificationApproved();
        if (result && result.success) {
          window.open(
            "https://docs.google.com/forms/d/e/1FAIpQLSfDnIguNsvFCxGBYnb4n_Q7sZLTTn3ze3fSaGO0G9ph1HmnAA/viewform?usp=sf_link",
            "_self",
          );
        }
      }
    },
  }
</script>

<style lang="scss">

    @import './common/variables';

    body,html {
        background-color: $white;
        min-height: 100%;
    }

    #app {
        width: 100%;
        min-height: calc(100% - 80px);
        font-family: $font;
        &.relative {
          position: relative;
        }
        a {
            color: $primary-blue;
        }
    }

    *, *::before, *::after {
        box-sizing: border-box;
    }

    :focus{outline: none;}

    label {
        margin: 0;
    }

    .reset table,
    .reset caption,
    .reset thead,
    .reset tbody,
    .reset tfoot,
    .reset tr,
    .reset th,
    .reset td {
        background-color: transparent;
        margin: 0;
        padding: 0;
        border: 0;
        font-size: 100%;
        font: inherit;
        color: $dark-blue;
        vertical-align: baseline;
    }
    table.reset {
        border-collapse: collapse;
        border-spacing: 0;
    }

    /**
        overrides for RT original styles
     */

    #pageErrors-errors { /* /user/teacherDetails - blue message above */
        display: none;
    }

    input[type=text],
    input[type=password] {
        border-radius: 0;
    }

    input[type=password]:focus,
    input[type=text]:focus,
    input[type=text]:invalid {
        border: 0px solid transparent;
        border-bottom: 1px solid $dark-grey;
        background-color: transparent;
    }

    input[type=text]:hover,
    input[type=password]:hover {
        border: 0 solid transparent;
        border-bottom: 1px solid $dark-grey !important;
    }

    /**

     */

    //select
    .rt-select {

        margin-top: 2.3rem;
        margin-bottom: 1.3rem;

        .vs__search::placeholder,
        .vs__dropdown-toggle {
            /*background-color: transparent;*/
            font-size: 1rem;
            border: 0;
            padding: 3.8px 2px 3.3px;
            margin: 1.25rem 0 0;
            font-weight: 300;
            width: 100%;
            border-bottom: 1px solid $dark-grey;
            line-height: 1.1rem;
            border-radius: 0;
            font-family: $font;
        }

        .vs__dropdown-menu {
            border-top: 1px solid $primary-blue;
            max-height: 10rem;
        }

        .vs__dropdown-option--highlight {
            background-color: $primary-blue;
        }

        .vs__selected {
            position: relative;
            right: 4px;
            padding: 0;
            font-weight: 400;
            font-family: $font;
        }

        .vs__search {
            position: relative;
            right: 4px;
            padding-left: 0;
        }

        .vs__search::placeholder {
            color: $grey;
        }


        .vs__clear,
        .vs__open-indicator {
            /*fill: #394066;*/
        }
    }

</style>
